import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';

/**
 * Authentication method type
 */
export type AuthMethodType = 'managed_key' | 'pkey' | 'password';

/**
 * Managed key data structure
 */
export interface ManagedKeyData {
  /** Public key generated by the system */
  publicKey: string | null;
  /** Fingerprint of the public key */
  fingerprint: string | null;
  /** Snowflake account ID associated with managed key */
  accountId: number | null;
  /** Whether key generation is pending */
  isPending: boolean;
}

/**
 * Account validation state
 */
export interface AccountValidationState {
  /** Whether validation is currently in progress */
  isValidating: boolean;
  /** Whether the account identifier is valid */
  isValid: boolean;
  /** Whether the account identifier is invalid */
  isInvalid: boolean;
  /** Error message if validation failed */
  errorMessage: string | null;
}

/**
 * UI state for SQL explanation
 */
export interface SqlExplanationState {
  /** Whether SQL explanation is expanded */
  isExpanded: boolean;
}

/**
 * UI state for setup help panels
 */
export interface SetupHelpPanelsState {
  /** Whether role setup help is expanded */
  isRoleHelpExpanded: boolean;
  /** Whether username setup help is expanded */
  isUsernameHelpExpanded: boolean;
  /** Whether warehouse setup help is expanded */
  isWarehouseHelpExpanded: boolean;
}

/**
 * Confirmation state
 */
export interface ConfirmationState {
  /** Whether user has confirmed running SQL */
  hasSqlCompleted: boolean;
}

/**
 * State shape for the onboarding Redux slice
 */
interface OnboardingState {
  /** Current step in the onboarding flow */
  currentStep: 'snowflake_setup' | 'runner_provision' | 'complete';
  /** Whether the user has skipped onboarding */
  hasSkipped: boolean;
  /** Journey context containing app information */
  journeyContext: {
    /** Display name of the application */
    app_name: string;
    /** Journey variant identifier */
    app_variant: string;
    /** URL path to redirect to after onboarding */
    redirect_to: string;
  } | null;
  /** Whether the skip confirmation dialog is visible */
  showSkipConfirmation: boolean;
  /** ID of the Snowflake account created during onboarding */
  snowflakeAccountId: number | null;
  /** Data related to managed key authentication */
  managedKeyData: ManagedKeyData | null;
  /** Currently selected authentication method */
  selectedAuthMethod: AuthMethodType;
  /** Account validation state */
  accountValidation: AccountValidationState;
  /** SQL explanation UI state */
  sqlExplanation: SqlExplanationState;
  /** Setup help panels UI state */
  setupHelpPanels: SetupHelpPanelsState;
  /** Confirmation state */
  confirmation: ConfirmationState;
  /** Cached account IDs by authentication type (stored in localStorage) */
  cachedAccountIds: {
    managed_key: number | null;
    pkey: number | null;
    password: number | null;
  };
  /** Whether SQL section is shown in managed key form */
  showSqlSection: boolean;
}

/**
 * Initial state for the onboarding slice
 */
const initialState: OnboardingState = {
  currentStep: 'snowflake_setup',
  hasSkipped: false,
  journeyContext: null,
  showSkipConfirmation: false,
  snowflakeAccountId: null,
  managedKeyData: null,
  selectedAuthMethod: 'managed_key',
  accountValidation: {
    isValidating: false,
    isValid: false,
    isInvalid: false,
    errorMessage: null,
  },
  sqlExplanation: {
    isExpanded: false,
  },
  setupHelpPanels: {
    isRoleHelpExpanded: false,
    isUsernameHelpExpanded: false,
    isWarehouseHelpExpanded: false,
  },
  confirmation: {
    hasSqlCompleted: false,
  },
  cachedAccountIds: {
    managed_key: null,
    pkey: null,
    password: null,
  },
  showSqlSection: false,
};

/**
 * Onboarding Redux Slice
 *
 * Manages the state for the user onboarding flow, including:
 * - Current step tracking (Snowflake setup, runner provision, complete)
 * - Journey context (CICD vs DBT journey information)
 * - Skip confirmation dialog state
 * - Skip status tracking
 *
 * @slice
 */
const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    /**
     * Sets the journey context and resets onboarding state
     *
     * Called when journey context is parsed from URL or Auth0 metadata.
     * Resets the current step to 'snowflake_setup' and clears snowflake account ID.
     * Note: Does not reset skip status to preserve user's skip choice.
     *
     * @param state - Current onboarding state
     * @param action - Action containing journey context payload
     */
    setJourneyContext: (state, action: PayloadAction<OnboardingState['journeyContext']>) => {
      state.journeyContext = action.payload;
      state.currentStep = 'snowflake_setup';
      state.snowflakeAccountId = null;
    },
    /**
     * Sets the Snowflake account ID after successful account creation
     *
     * Called when the Snowflake account is successfully created during onboarding.
     * This ID is needed for runner provisioning.
     *
     * @param state - Current onboarding state
     * @param action - Action containing the snowflake account ID
     */
    setSnowflakeAccountId: (state, action: PayloadAction<number>) => {
      state.snowflakeAccountId = action.payload;
    },
    /**
     * Completes the current step and advances to the next
     *
     * Step progression:
     * - 'snowflake_setup' -> 'runner_provision'
     * - 'runner_provision' -> 'complete'
     *
     * @param state - Current onboarding state
     * @param action - Action containing the step to complete
     */
    completeStep: (state, action: PayloadAction<OnboardingState['currentStep']>) => {
      if (action.payload === 'snowflake_setup') {
        state.currentStep = 'runner_provision';
      } else if (action.payload === 'runner_provision') {
        state.currentStep = 'complete';
      }
    },
    /**
     * Marks onboarding as skipped
     *
     * Sets hasSkipped to true, allowing the app to track that the user
     * opted out of onboarding. This can be used to show appropriate messaging
     * or allow re-entry to onboarding later.
     *
     * @param state - Current onboarding state
     */
    skipOnboarding: (state) => {
      state.hasSkipped = true;
    },
    /**
     * Toggles the skip confirmation dialog visibility
     *
     * @param state - Current onboarding state
     * @param action - Action containing boolean for dialog visibility
     */
    setShowSkipConfirmation: (state, action: PayloadAction<boolean>) => {
      state.showSkipConfirmation = action.payload;
    },
    /**
     * Sets the selected authentication method
     *
     * @param state - Current onboarding state
     * @param action - Action containing the authentication method
     */
    setSelectedAuthMethod: (state, action: PayloadAction<AuthMethodType>) => {
      state.selectedAuthMethod = action.payload;
    },
    /**
     * Sets managed key data after key generation
     *
     * @param state - Current onboarding state
     * @param action - Action containing managed key data
     */
    setManagedKeyData: (state, action: PayloadAction<ManagedKeyData>) => {
      state.managedKeyData = action.payload;
    },
    /**
     * Clears managed key data
     *
     * @param state - Current onboarding state
     */
    clearManagedKeyData: (state) => {
      state.managedKeyData = null;
    },
    /**
     * Updates managed key pending status
     *
     * @param state - Current onboarding state
     * @param action - Action containing pending status
     */
    setManagedKeyPending: (state, action: PayloadAction<boolean>) => {
      if (state.managedKeyData != null) {
        state.managedKeyData.isPending = action.payload;
      }
    },
    /**
     * Sets account validation loading state
     *
     * @param state - Current onboarding state
     * @param action - Action containing validating status
     */
    setAccountValidating: (state, action: PayloadAction<boolean>) => {
      state.accountValidation.isValidating = action.payload;
      if (action.payload) {
        // Clear previous validation results when starting new validation
        state.accountValidation.isValid = false;
        state.accountValidation.isInvalid = false;
        state.accountValidation.errorMessage = null;
      }
    },
    /**
     * Sets account validation success
     *
     * @param state - Current onboarding state
     */
    setAccountValid: (state) => {
      state.accountValidation.isValidating = false;
      state.accountValidation.isValid = true;
      state.accountValidation.isInvalid = false;
      state.accountValidation.errorMessage = null;
    },
    /**
     * Sets account validation failure
     *
     * @param state - Current onboarding state
     * @param action - Action containing error message
     */
    setAccountInvalid: (state, action: PayloadAction<string>) => {
      state.accountValidation.isValidating = false;
      state.accountValidation.isValid = false;
      state.accountValidation.isInvalid = true;
      state.accountValidation.errorMessage = action.payload;
    },
    /**
     * Clears account validation state
     *
     * @param state - Current onboarding state
     */
    clearAccountValidation: (state) => {
      state.accountValidation.isValidating = false;
      state.accountValidation.isValid = false;
      state.accountValidation.isInvalid = false;
      state.accountValidation.errorMessage = null;
    },
    /**
     * Sets SQL explanation expanded state
     *
     * @param state - Current onboarding state
     * @param action - Action containing expanded status
     */
    setSqlExplanationExpanded: (state, action: PayloadAction<boolean>) => {
      state.sqlExplanation.isExpanded = action.payload;
    },
    /**
     * Sets role help panel expanded state
     *
     * @param state - Current onboarding state
     * @param action - Action containing expanded status
     */
    setRoleHelpExpanded: (state, action: PayloadAction<boolean>) => {
      state.setupHelpPanels.isRoleHelpExpanded = action.payload;
    },
    /**
     * Sets username help panel expanded state
     *
     * @param state - Current onboarding state
     * @param action - Action containing expanded status
     */
    setUsernameHelpExpanded: (state, action: PayloadAction<boolean>) => {
      state.setupHelpPanels.isUsernameHelpExpanded = action.payload;
    },
    /**
     * Sets warehouse help panel expanded state
     *
     * @param state - Current onboarding state
     * @param action - Action containing expanded status
     */
    setWarehouseHelpExpanded: (state, action: PayloadAction<boolean>) => {
      state.setupHelpPanels.isWarehouseHelpExpanded = action.payload;
    },
    /**
     * Sets SQL confirmation status
     *
     * @param state - Current onboarding state
     * @param action - Action containing confirmation status
     */
    setSqlConfirmation: (state, action: PayloadAction<boolean>) => {
      state.confirmation.hasSqlCompleted = action.payload;
    },
    /**
     * Caches account ID for authentication type
     *
     * @param state - Current onboarding state
     * @param action - Action containing auth type and account ID
     */
    setCachedAccountId: (state, action: PayloadAction<{ authType: AuthMethodType; accountId: number | null }>) => {
      state.cachedAccountIds[action.payload.authType] = action.payload.accountId;
    },
    /**
     * Clears cached account ID for authentication type
     *
     * @param state - Current onboarding state
     * @param action - Action containing auth type
     */
    clearCachedAccountId: (state, action: PayloadAction<AuthMethodType>) => {
      state.cachedAccountIds[action.payload] = null;
    },
    /**
     * Sets whether SQL section is shown in managed key form
     *
     * @param state - Current onboarding state
     * @param action - Action containing show status
     */
    setShowSqlSection: (state, action: PayloadAction<boolean>) => {
      state.showSqlSection = action.payload;
    },
    /**
     * Resets onboarding state to initial values
     *
     * Useful for testing or when a user wants to restart the onboarding process.
     *
     * @param state - Current onboarding state
     */
    resetOnboarding: (state) => {
      Object.assign(state, initialState);
    },
  },
});

/**
 * Action creators exported from the onboarding slice
 */
export const {
  setJourneyContext,
  setSnowflakeAccountId,
  completeStep,
  skipOnboarding,
  setShowSkipConfirmation,
  setSelectedAuthMethod,
  setManagedKeyData,
  clearManagedKeyData,
  setManagedKeyPending,
  setAccountValidating,
  setAccountValid,
  setAccountInvalid,
  clearAccountValidation,
  setSqlExplanationExpanded,
  setRoleHelpExpanded,
  setUsernameHelpExpanded,
  setWarehouseHelpExpanded,
  setSqlConfirmation,
  setCachedAccountId,
  clearCachedAccountId,
  setShowSqlSection,
  resetOnboarding,
} = onboardingSlice.actions;

/**
 * Selector to get the current onboarding step
 *
 * @param state - Redux root state
 * @returns Current step in onboarding flow
 */
export const selectCurrentStep = (state: RootState): OnboardingState['currentStep'] => state.onboarding.currentStep;

/**
 * Selector to get the journey context
 *
 * @param state - Redux root state
 * @returns Journey context or null if not set
 */
export const selectJourneyContext = (state: RootState): OnboardingState['journeyContext'] =>
  state.onboarding.journeyContext;

/**
 * Selector to get the skip confirmation dialog visibility
 *
 * @param state - Redux root state
 * @returns Whether the skip confirmation dialog is visible
 */
export const selectShowSkipConfirmation = (state: RootState): boolean => state.onboarding.showSkipConfirmation;

/**
 * Selector to check if user has skipped onboarding
 *
 * @param state - Redux root state
 * @returns Whether the user has skipped onboarding
 */
export const selectHasSkipped = (state: RootState): boolean => state.onboarding.hasSkipped;

/**
 * Selector to get the Snowflake account ID
 *
 * @param state - Redux root state
 * @returns The Snowflake account ID or null if not set
 */
export const selectSnowflakeAccountId = (state: RootState): number | null => state.onboarding.snowflakeAccountId;

/**
 * Selector to get the selected authentication method
 *
 * @param state - Redux root state
 * @returns The currently selected authentication method
 */
export const selectSelectedAuthMethod = (state: RootState): AuthMethodType => state.onboarding.selectedAuthMethod;

/**
 * Selector to get managed key data
 *
 * @param state - Redux root state
 * @returns The managed key data or null if not set
 */
export const selectManagedKeyData = (state: RootState): ManagedKeyData | null => state.onboarding.managedKeyData;

/**
 * Selector to get account validation state
 *
 * @param state - Redux root state
 * @returns The account validation state
 */
export const selectAccountValidation = (state: RootState): AccountValidationState => state.onboarding.accountValidation;

/**
 * Selector to get whether account validation is in progress
 *
 * @param state - Redux root state
 * @returns Whether account validation is in progress
 */
export const selectIsAccountValidating = (state: RootState): boolean => state.onboarding.accountValidation.isValidating;

/**
 * Selector to get whether account is valid
 *
 * @param state - Redux root state
 * @returns Whether account is valid
 */
export const selectIsAccountValid = (state: RootState): boolean => state.onboarding.accountValidation.isValid;

/**
 * Selector to get whether account is invalid
 *
 * @param state - Redux root state
 * @returns Whether account is invalid
 */
export const selectIsAccountInvalid = (state: RootState): boolean => state.onboarding.accountValidation.isInvalid;

/**
 * Selector to get account validation error message
 *
 * @param state - Redux root state
 * @returns Account validation error message or null
 */
export const selectAccountValidationError = (state: RootState): string | null =>
  state.onboarding.accountValidation.errorMessage;

/**
 * Selector to get SQL explanation state
 *
 * @param state - Redux root state
 * @returns The SQL explanation state
 */
export const selectSqlExplanation = (state: RootState): SqlExplanationState => state.onboarding.sqlExplanation;

/**
 * Selector to get whether SQL explanation is expanded
 *
 * @param state - Redux root state
 * @returns Whether SQL explanation is expanded
 */
export const selectIsSqlExplanationExpanded = (state: RootState): boolean => state.onboarding.sqlExplanation.isExpanded;

/**
 * Selector to get confirmation state
 *
 * @param state - Redux root state
 * @returns The confirmation state
 */
export const selectConfirmation = (state: RootState): ConfirmationState => state.onboarding.confirmation;

/**
 * Selector to get whether SQL has been completed
 *
 * @param state - Redux root state
 * @returns Whether SQL has been completed
 */
export const selectHasSqlCompleted = (state: RootState): boolean => state.onboarding.confirmation.hasSqlCompleted;

/**
 * Selector to get cached account IDs
 *
 * @param state - Redux root state
 * @returns Cached account IDs by authentication type
 */
export const selectCachedAccountIds = (
  state: RootState,
): {
  managed_key: number | null;
  pkey: number | null;
  password: number | null;
} => state.onboarding.cachedAccountIds;

/**
 * Selector to get cached account ID for specific auth type
 *
 * @param state - Redux root state
 * @param authType - Authentication type
 * @returns Cached account ID or null
 */
export const selectCachedAccountIdForAuthType = (state: RootState, authType: AuthMethodType): number | null =>
  state.onboarding.cachedAccountIds[authType];

/**
 * Selector to get whether SQL section is shown in managed key form
 *
 * @param state - Redux root state
 * @returns Whether SQL section is shown
 */
export const selectShowSqlSection = (state: RootState): boolean => state.onboarding.showSqlSection;

/**
 * Selector to get whether role help panel is expanded
 *
 * @param state - Redux root state
 * @returns Whether role help panel is expanded
 */
export const selectIsRoleHelpExpanded = (state: RootState): boolean =>
  state.onboarding.setupHelpPanels.isRoleHelpExpanded;

/**
 * Selector to get whether username help panel is expanded
 *
 * @param state - Redux root state
 * @returns Whether username help panel is expanded
 */
export const selectIsUsernameHelpExpanded = (state: RootState): boolean =>
  state.onboarding.setupHelpPanels.isUsernameHelpExpanded;

/**
 * Selector to get whether warehouse help panel is expanded
 *
 * @param state - Redux root state
 * @returns Whether warehouse help panel is expanded
 */
export const selectIsWarehouseHelpExpanded = (state: RootState): boolean =>
  state.onboarding.setupHelpPanels.isWarehouseHelpExpanded;

export default onboardingSlice.reducer;
